import * as React from 'react';

// Category pill filter config — maps display labels to substrings that appear in GrapesJS category names
const CATEGORIES = [
  { id: 'all',          label: 'All' },
  { id: 'layout',       label: 'Layout',      match: /layout|nav|typography|basic|element/i },
  { id: 'hero',         label: 'Hero',         match: /hero/i },
  { id: 'property',     label: 'Property',     match: /property|listing|search|filter/i },
  { id: 'content',      label: 'Content',      match: /content|media|about|team|form|contact/i },
  { id: 'components',   label: 'Components',   match: /component|ui|react|modal|tab|accordion/i },
  { id: 'templates',    label: 'Templates',    match: /template|page/i },
  { id: 'integrations', label: 'Integrations', match: /integrat|map|translate|analytic|calendly|stripe/i },
];

function categoryId(catName) {
  for (const c of CATEGORIES.slice(1)) {
    if (c.match.test(catName)) return c.id;
  }
  return 'all';
}

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>
  </svg>
);

export default function CustomBlockManager({ mapCategoryBlocks, dragStart, dragStop }) {
  const [search, setSearch] = React.useState('');
  const [activeCat, setActiveCat] = React.useState('all');

  // Build filtered sections
  const sections = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = [];

    Array.from(mapCategoryBlocks).forEach(([category, blocks]) => {
      // Apply search filter
      const matched = q
        ? blocks.filter((b) => b.getLabel().toLowerCase().includes(q))
        : blocks;
      if (!matched.length) return;

      // Apply category pill filter
      if (activeCat !== 'all' && categoryId(category) !== activeCat) return;

      result.push({ category, blocks: matched });
    });

    return result;
  }, [mapCategoryBlocks, search, activeCat]);

  const totalBlocks = React.useMemo(
    () => Array.from(mapCategoryBlocks.values()).reduce((s, b) => s + b.length, 0),
    [mapCategoryBlocks]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Search */}
      <div className="dx-block-search">
        <SearchIcon />
        <input
          placeholder={`Search ${totalBlocks} blocks…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dx-text-3)', padding: 0, display: 'flex' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        )}
      </div>

      {/* Category pills */}
      <div className="dx-cat-pills">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            className={`dx-cat-pill${activeCat === c.id ? ' active' : ''}`}
            onClick={() => setActiveCat(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {sections.length === 0 && (
        <div className="dx-block-empty">
          <SearchIcon />
          <div>No blocks match{search ? ` "${search}"` : ' this filter'}</div>
        </div>
      )}

      {/* Sections */}
      {sections.map(({ category, blocks }) => (
        <div key={category} className="dx-block-section">
          <div className="dx-block-section-h">
            <span>{category}</span>
            <span className="dx-block-section-count">{blocks.length}</span>
          </div>
          <div className="dx-block-grid">
            {blocks.map((block) => (
              <div
                key={block.getId()}
                className="dx-block-tile"
                draggable
                onDragStart={(ev) => dragStart(block, ev.nativeEvent)}
                onDragEnd={() => dragStop(false)}
                title={block.getLabel()}
              >
                <div className="dx-block-thumb">
                  <BlockThumb block={block} />
                </div>
                <div className="dx-block-label">{block.getLabel()}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Blocks whose content is a plain HTML string get a real scaled-down preview
// instead of a generic icon. Content is rendered outside the GrapesJS canvas
// iframe, so any embedded <style> tags must be stripped first — otherwise
// their rules would apply globally to the whole app document, not just this
// thumbnail (the block manager renders every tile's content at once).
// pointer-events is disabled on the preview so native-draggable inner
// elements (e.g. <img>) can never hijack the block-drag gesture that starts
// on the tile itself.
function stripStyleTags(html) {
  return html.replace(/<style[\s\S]*?<\/style>/gi, '');
}

// A single leaf element with nothing but text (a bare <button>Default</button>
// or <h1>Small</h1>) renders at its own natural, tiny content size positioned
// top-left of the 400px preview canvas -- after the 0.35 scale it's a few
// specks of near-invisible text in an otherwise blank white box. Blocks whose
// content has actual nested structure (a real <ul>/<li> or <div>/<img> tree)
// fill enough of that canvas to read as a real preview, which is why
// Accordion/Card already look fine. Detect the trivial-leaf case and fall
// back to the icon instead of a preview that's effectively blank.
function isTrivialLeafContent(html) {
  const match = html.trim().match(/^<([a-zA-Z0-9]+)[^>]*>([\s\S]*)<\/\1>\s*$/);
  if (!match) return false;
  return !/<[a-zA-Z]/.test(match[2]);
}

function BlockThumb({ block }) {
  const content = typeof block.getContent === 'function' ? block.getContent() : null;
  if (typeof content !== 'string' || !content.trim() || isTrivialLeafContent(content)) {
    return <BlockMedia media={block.getMedia()} />;
  }

  return (
    <div className="dx-block-preview" aria-hidden="true">
      <div
        className="dx-block-preview-inner"
        dangerouslySetInnerHTML={{ __html: stripStyleTags(content) }}
      />
    </div>
  );
}

function BlockMedia({ media }) {
  if (!media) return <FallbackIcon />;
  const trimmed = media.trim();
  if (trimmed.startsWith('<svg')) {
    const sized = trimmed.replace(/<svg/, '<svg style="width:20px;height:20px;display:block;flex-shrink:0;color:var(--dx-text-2)"');
    return <span dangerouslySetInnerHTML={{ __html: sized }} />;
  }
  if (trimmed.startsWith('<img')) {
    const sized = trimmed.replace(/<img/, '<img style="width:22px;height:22px;object-fit:contain;display:block"');
    return <span dangerouslySetInnerHTML={{ __html: sized }} />;
  }
  return <FallbackIcon />;
}

function FallbackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--dx-text-2)' }}>
      <rect x="3" y="3" width="6" height="6" rx="1"/><rect x="15" y="3" width="6" height="6" rx="1"/>
      <rect x="3" y="15" width="6" height="6" rx="1"/><rect x="15" y="15" width="6" height="6" rx="1"/>
    </svg>
  );
}
